import { before, after } from './bits'

import deline from './deline'
import envRaw from './env-raw'
import Emitter from './emitter'
import MapSimple from './map-simple'
import forceResize from './force-resize'
import extractRawElm from './extract-raw-elm'

import { offEvent, onEventPassive } from './on-event'
import { getElmStr, getElmSign, getElmStrShort } from './get-elm-str'

const cache = MapSimple()

const SCROLL_FINISHED = true
const SCROLL_IN_PROGRESS = false

const TIMEOUT_DELAY = 100

ScrollDetector.cache = cache

export default function ScrollDetector($elm) {
  const elm = extractRawElm($elm)

  if (cache.has(elm)) {
    return cache.get(elm)
  }

  let lastLeft = null
  let lastTop = null

  let count = 0
  let touched = false
  let destroyed = false
  let timeoutId = null

  let topInProgress = false
  let leftInProgress = false
  let someInProgress = false

  const detector = cache.set(elm, Emitter(`scrollDetector[elm=${getElmStrShort(elm)}`))

  detector.getLeft = getLeft
  detector.getTop = getTop
  detector.manualTrigger = manualTrigger

  detector.on = before(beforeOn, detector.on)
  detector.pre = before(beforeOn, detector.pre)
  detector.after = before(beforeOn, detector.after)
  detector.off = after(detector.off, afterOff)
  detector.clear = after(detector.clear, afterOff)

  // left
  detector.left = Emitter(`scrollDetector[elm=${getElmStrShort(elm)}::left`)

  detector.left.on = before(beforeOn, detector.left.on)
  detector.left.pre = before(beforeOn, detector.left.pre)
  detector.left.after = before(beforeOn, detector.left.after)
  detector.left.off = after(detector.left.off, afterOff)
  detector.left.clear = after(detector.left.clear, afterOff)

  // top
  detector.top = Emitter(`scrollDetector[elm=${getElmStrShort(elm)}::top`)

  detector.top.on = before(beforeOn, detector.top.on)
  detector.top.pre = before(beforeOn, detector.top.pre)
  detector.top.after = before(beforeOn, detector.top.after)
  detector.top.off = after(detector.top.off, afterOff)
  detector.top.clear = after(detector.top.clear, afterOff)

  return detector

  // methods

  function getActualLeft() {
    return Math.round(elm.scrollLeft)
  }

  function getActualTop() {
    return Math.round(elm.scrollTop)
  }

  function getLeft() {
    return lastLeft !== null ? lastLeft : getActualLeft()
  }

  function getTop() {
    return lastTop !== null ? lastTop : getActualTop()
  }

  function manualTrigger() {
    lastLeft = null
    lastTop = null
    scrollHandler()
  }

  function tryScrollEnd() {
    if (!touched && lastLeft !== null && lastTop !== null) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(timeoutHandler, TIMEOUT_DELAY, count)
    }
  }

  function timeoutHandler(lastCount) {
    if (count === lastCount) {
      if (someInProgress) detector.emit(lastLeft, lastTop, SCROLL_FINISHED)
      if (leftInProgress) detector.left.emit(lastLeft, lastTop, SCROLL_FINISHED)
      if (topInProgress) detector.top.emit(lastLeft, lastTop, SCROLL_FINISHED)

      someInProgress = false
      leftInProgress = false
      topInProgress = false

      if (envRaw.showsScrollDetectorDebug) {
        console.debug(deline(`
          ${Date.now()} !scroll on "${getElmSign(elm)}" FINISHED:
            [${leftInProgress ? 'L' : '-'}]
            [${topInProgress ? 'T' : '-'}]:
            { left: ${lastLeft}, top: ${lastTop} }`))
      }
    }
  }

  function scrollHandler() {
    const left = getActualLeft()
    const top = getActualTop()

    const leftChanged = left !== lastLeft
    const topChanged = top !== lastTop
    const someChanged = topChanged || leftChanged

    someInProgress = someInProgress || someChanged
    leftInProgress = leftInProgress || leftChanged
    topInProgress = topInProgress || topChanged

    if (leftChanged) lastLeft = left
    if (topChanged) lastTop = top

    if (someChanged) detector.emit(lastLeft, lastTop, SCROLL_IN_PROGRESS)
    if (leftChanged) detector.left.emit(lastLeft, lastTop, SCROLL_IN_PROGRESS)
    if (topChanged) detector.top.emit(lastLeft, lastTop, SCROLL_IN_PROGRESS)

    count++
    tryScrollEnd()

    if (envRaw.showsScrollDetectorDebug) {
      console.debug(deline(`
        ${Date.now()} !scroll on "${getElmSign(elm)}" IN_PROGRESS:
          [${leftChanged ? 'L' : '-'}]
          [${topChanged ? 'T' : '-'}]:
          { left: ${lastLeft}, top: ${lastTop} }`))
    }
  }

  function touchendHandler() {
    count++
    touched = false
    tryScrollEnd()
  }

  function touchstartHandler() {
    count++
    touched = true
  }

  function beforeOn() {
    if (destroyed) {
      throw new Error(`ScrollDetector: already destroyed! ${getElmStr(elm)}`)
    }

    const allDetached = true
      && detector.size() === 0
      && detector.left.size() === 0
      && detector.top.size() === 0

    if (allDetached) {
      onEventPassive(elm, 'scroll', scrollHandler)
      onEventPassive(elm, 'touchend', touchendHandler)
      onEventPassive(elm, 'touchstart', touchstartHandler)
      forceResize.emitter.on(manualTrigger)
    }
  }

  function afterOff() {
    const allDetached = true
      && detector.size() === 0
      && detector.left.size() === 0
      && detector.top.size() === 0

    if (!destroyed && allDetached) {
      offEvent(elm, 'scroll', scrollHandler)
      offEvent(elm, 'touchend', touchendHandler)
      offEvent(elm, 'touchstart', touchstartHandler)
      forceResize.emitter.off(manualTrigger)
      cache.del(elm)
      destroyed = true
    }
  }
}
